<template>
  <div :class="[$attrs.class]" v-if="isVisible">
    <div class="tw-relative">
      <div v-if="props.layoutType != 'condensed'" :class="['tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-6 tw-transition-all tw-duration-300', barClasses]"></div>
      <div v-if="props.layoutType != 'condensed'" :class="['tw-absolute tw-left-0 tw-top-0 tw-bottom-0 tw-w-6 tw-transition-all tw-duration-300 tw-bg-orange-medium tw-z-10', {'tw-opacity-0': !activeBar}, {'tw-opacity-100': activeBar}]"></div>
      <div :class="[{'tw-pl-16': props.layoutType != 'condensed'}]">
        <div :class="[{'tw-mb-4': props.layoutType == 'condensed'}, {'tw-mb-8': props.layoutType != 'condensed'}, {'tw-pt-16': props.optionGroup.parentId != null}]">
          <h3 :class="[{'tw-text-3xl tw-mb-2': props.layoutType != 'condensed'}, {'tw-normal-case': props.layoutType == 'condensed'}, 'tw-font-bold']">
            {{optionGroup.description}}
          </h3>

          <template v-if="props.layoutType != 'condensed'">
            <template v-if="optionGroup.explanationtext != null && optionGroup.explanationtext != ''">
              <div class="tw-text-2xl tw-mb-2">
                {{optionGroup.explanationtext}}
              </div>
            </template>

            <template v-if="(optionGroup.minselects && optionGroup.minselects == 1 && optionGroup.maxselects && optionGroup.maxselects == 1) || optionGroup.mandatory">
              <div :class="{'tw-text-red-400 tw-font-bold': totalSelected === 0,'tw-text-gray-500 tw-font-normal': totalSelected > 0}">
                Choose 1
              </div>
            </template>
            <template v-else-if="optionGroup.maxselects">
              <div class="tw-text-lg tw-text-gray-500">
                Choose up to {{optionGroup.maxselects}} | Currently Selected: {{totalSelected}}
              </div>
            </template>
          </template>
        </div>

        <div v-if="validationError">
          {{validationError}}
        </div>

        <template v-if="props.layoutType == 'condensed'">
          <select
            v-model="selectFieldOption"
            :name="'optSelect-' + optionGroup.id"
            class="tw-block tw-text-center tw-py-4 tw-w-full tw-font-bold tw-appearance-none focus:tw-outline-none tw-border-gray-400 focus:tw-border-orange-medium focus:tw-ring-0 tw-bg-transparent tw-text-2xl">>
            <option 
              v-for="option in optionGroup.options" 
              :key="'select-opt-'+option.id" 
              :value="option">
              {{option.name}}
            </option>
          </select>
        </template>

        <template v-else>
          <div class="tw-grid tw-gap-8 tw-grid-cols-2">  
            <Option v-for="option in optionGroup.options"
              :class="['tw-bg-white tw-shadow-lg tw-flex tw-items-center tw-justify-center tw-transition-all tw-cursor-pointer', {'tw-px-8 tw-py-8 tw-rounded-3xl': props.layoutType == 'full'}, {'tw-px-4 tw-py-3 tw-rounded-xl': props.layoutType == 'condensed'}]"
              :optionQuantities="(optionGroup.maxchoicequantity != undefined) && (optionGroup.maxchoicequantity > 1)"
              @quantityUp="incrementOptionQuantity"
              @quantityDown="decrementOptionQuantity"
              @optionToggled="toggleOption"
              @inverseToggled="toggleInverse"
              :key="'opt-'+option.id"
              :layoutType="'condensed'"
              :option="option" />
          </div>
        </template>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed, watch } from 'vue'
  import { useRouter } from 'vue-router'
  import type OptionGroupInterface from '../../types/OptionGroup';
  import type OptionInterface from '../../types/Option';

  import Option from './_Option.vue';

  const props = defineProps<{ 
    optionGroup: OptionGroupInterface, 
    isFirst: Boolean, 
    selectedOptions: Map<number, number> ,
    modularNutritionData: Boolean,
    layoutType: String,
  }>();

  const emit = defineEmits(['optionToggled', 'inverseToggled', 'optionAdded', 'optionRemoved'])

  const validationError = ref('');
  const optionQuantity = ref(0);
  const selectFieldOption = ref(undefined as (undefined | OptionInterface));

  onMounted(() => { 
    if (props.optionGroup.options) {
      selectFieldOption.value = props.optionGroup.options[0]
    }
  })

  watch(() => selectFieldOption.value, (opt) => { 
    if (props.layoutType == 'condensed') {
      if (typeof(opt) != 'undefined' && !opt.isSelected) {
        optGroupSelectChanged(opt)
      }
    }
  })

  const optGroupSelectChanged = ((opt: OptionInterface) => {
    if (props.layoutType == 'condensed') {
      toggleOption(opt)
    }
  })

  const toggleOption = ((option: OptionInterface) => {
    let toggleSelectedOn = !option.isSelected;

    if (!toggleSelectedOn) {
      // Make sure we can't select multiple of the same option
      if ((props.optionGroup.maxchoicequantity != undefined) && (props.optionGroup.maxchoicequantity > 1)) {
        option.quantity +=1;
        emit('optionAdded', option);
      } else {
        // We can always turn off
        option.isSelected = !option.isSelected;
        option.quantity = 0;
        emit('optionRemoved', option);
      }
    } else {
      let currentSelections = props.optionGroup.options.filter((opt: OptionInterface) => {
        return opt.isSelected;
      })

      if (props.optionGroup.mandatory) {
        if (currentSelections.length > 0) {
          // Deselect the other option
          currentSelections.forEach((opt: OptionInterface) => {
            if (opt.isSelected) {
              opt.isSelected = false;
              opt.quantity = 0;
              emit('optionRemoved', opt);
            }
          })
        }
        option.isSelected = true;
        option.quantity = 1;
        emit('optionAdded', option);
      } else if (props.optionGroup.maxselects && props.optionGroup.maxselects > 0) {
        if (totalSelected.value >= props.optionGroup.maxselects) {
          // We do nothing
        } else {
          option.isSelected = true;
          option.quantity = 1;
          emit('optionAdded', option);
        }
      }
    }
  })

  const toggleInverse = ((option: OptionInterface) => {
    let toggleSelectedOn = !option.isSelected;

    if (!toggleSelectedOn) {
      // We can always turn off
      emit('optionAdded', option);
      option.isSelected = false;
    } else {
      let currentSelections = props.optionGroup.options.filter((opt: OptionInterface) => {
        return opt.isSelected;
      })

      if (props.optionGroup.mandatory) {
        if (currentSelections.length > 0) {
          // Deselect the other option
          currentSelections.forEach((opt: OptionInterface) => {
            opt.isSelected = true;
          })
        }
        emit('optionRemoved', option);
        option.isSelected = true;
      }

      if (props.optionGroup.maxselects && props.optionGroup.maxselects > 0) {
        if (currentSelections.length >= props.optionGroup.maxselects) {

        } else {
        emit('optionRemoved', option);
          option.isSelected = true;
        }
      }
    }
  })

  const incrementOptionQuantity = ((option: OptionInterface) => {

    if ((props.optionGroup.maxchoicequantity != undefined) && (props.optionGroup.maxchoicequantity > 1)) {
      if (totalSelected.value + 1 <= props.optionGroup.maxchoicequantity) {
        option.quantity += 1;
        emit('optionAdded', option);
       } else {
        // We could alert the user that they have reached max selects?
       }
    } else {
      emit('optionAdded', option);
    }
  })

  const decrementOptionQuantity = ((option: OptionInterface) => {
     // emit('optionAdded', option);
     // option.quantity -= 1;
     if (option.quantity - 1 >= 1) {
      option.quantity -= 1;
      emit('optionRemoved', option);
     } else if (option.quantity - 1 < 1) {
      option.quantity = 0;
      option.isSelected = false;
      emit('optionRemoved', option);
     }
  })

  const isVisible = computed(() => {
    let visible = true;

    if (props.optionGroup.description.toLowerCase() == "included ingredients") {
      visible = false
    } else if (props.optionGroup.parentId != null && props.optionGroup.parentId != undefined) {
      // If the option group has a parent, that parent needs to either be selected or a inverted unselected option
      // TODO: Check for inverted unselected state on aprent
      visible = (props.selectedOptions.has(props.optionGroup.parentId));
    }

    return visible;
  })

  const hasChildren = computed(() => {
    let bool = false;
    let currentSelections = props.optionGroup.options.filter((opt: OptionInterface) => {
      return opt.isSelected;
    });

    if (currentSelections) {
      currentSelections.forEach((opt: OptionInterface) => {
        if (opt.optiongroups && opt.optiongroups.length > 0) {
          bool = true;
        }
      })
    }

    return bool;
  });

  const activeBar = computed(() => {
    let currentSelections = props.optionGroup.options.filter((opt: OptionInterface) => {
      return opt.isSelected;
    });

    let min = props.optionGroup.minselects ? props.optionGroup.minselects : 1;

    return (currentSelections && currentSelections.length >= min);
  })

  const barClasses = computed(() => {
    let cssClass = '';

    if (props.optionGroup.parentId != null) {
      cssClass = 'inactive-nested-optgroup' //'tw-bg-gradient-to-b tw-from-orange-medium tw-from-0% tw-via-parchment tw-via-1% tw-to-parchment tw-to-3%'
    } else if (activeBar.value) {
      cssClass = 'tw-bg-orange-medium';
    } else {
      cssClass = 'tw-bg-parchment';
    }

    return cssClass;
  })

  const totalSelected = computed(() => {
    let num = 0;
    let selected = props.optionGroup.options.forEach((opt: OptionInterface) => {
      if (opt.isSelected) {
        num += opt.quantity;
      }
    })

    return num;
  })

  watch(() => totalSelected.value, (count) => { 
    if (props.optionGroup.maxselects) {
      if (count >= props.optionGroup.maxselects) {
        props.optionGroup.options.forEach((opt: OptionInterface) => {
          if (!opt.isSelected) {
            opt.isDisabled = true;
          }
        })
      } else {
        props.optionGroup.options.forEach((opt: OptionInterface) => {
          if (!opt.isSelected) {
            opt.isDisabled = false;
          }
        })
      }
    }
  })

</script>

<style scoped>
  .inactive-nested-optgroup {
    background: #F48024;
    background: linear-gradient(180deg, #F48024 0%, rgba(219,217,217,1) 10%, rgba(219,217,217,1) 100%);
  }
</style>
